import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, tap, map } from 'rxjs';
import { 
    UnifiedAlert, 
    AlertSummary, 
    PaginatedAlertsResponse, 
    AlertFilters,
    BulkAlertAction,
    AlertTrendResponse,
    AlertTypeResponse,
    AlertCountryResponse,
    GeographicAlert,
    GeographicAlertsDashboard,
    GeographicAlertFilters,
    GeographicAlertStatus
} from './alerts.types';

@Injectable({ providedIn: 'root' })
export class AlertsService {

    // Private
    private _alerts: BehaviorSubject<UnifiedAlert[] | null> = new BehaviorSubject(null);
    private _selectedAlert: BehaviorSubject<UnifiedAlert | null> = new BehaviorSubject(null);
    private _alertSummary: BehaviorSubject<AlertSummary | null> = new BehaviorSubject(null);
    private _baseUrl = '/api';

    /**
     * Constructor
     */
    constructor(private _httpClient: HttpClient) {}

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for all alerts
     */
    get alerts$(): Observable<UnifiedAlert[]> {
        return this._alerts.asObservable();
    }

    /**
     * Getter for selected alert
     */
    get selectedAlert$(): Observable<UnifiedAlert> {
        return this._selectedAlert.asObservable();
    }

    /**
     * Getter for alert summary
     */
    get alertSummary$(): Observable<AlertSummary> {
        return this._alertSummary.asObservable();
    }

    /**
     * Setter for selected alert
     */
    set selectedAlert(value: UnifiedAlert) {
        this._selectedAlert.next(value);
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Get user's alerts with filters
     * @param filters - Optional filters for alerts
     * @returns Observable of paginated alerts
     */
    getMyAlerts(filters?: AlertFilters): Observable<PaginatedAlertsResponse> {
        let httpParams = new HttpParams();
        
        // Build query parameters
        if (filters) {
            Object.entries(filters).forEach(([key, value]) => {
                if (value !== undefined && value !== null && value !== '') {
                    // Handle severity as array (multiple values)
                    if (key === 'severity' && typeof value === 'string' && value.includes(',')) {
                        // Split comma-separated values and add multiple parameters
                        value.split(',').forEach(sev => {
                            httpParams = httpParams.append('severity', sev.trim());
                        });
                    } else {
                        httpParams = httpParams.set(key, value.toString());
                    }
                }
            });
        }
        
        // Debug: Log the constructed URL and params
        const url = `${this._baseUrl}/my-alerts`;
        console.log('🌐 API Request URL:', url);
        console.log('🌐 API Request Params:', httpParams.toString());
        console.log('🌐 Full URL:', url + '?' + httpParams.toString());
        
        return this._httpClient.get<PaginatedAlertsResponse>(
            url, 
            { params: httpParams }
        ).pipe(
            tap((response) => {
                this._alerts.next(response.data);
                console.log('✅ API Response received:', response.data.length, 'alerts');
                console.log('✅ API says filters_applied:', response.filters_applied || 'Not provided by API');
                console.log('✅ Full response structure:', {
                    success: response.success,
                    data_length: response.data.length,
                    pagination: response.pagination
                });
            })
        );
    }

    /**
     * Get alert summary for dashboard
     * @returns Observable of alert summary statistics
     */
    getAlertSummary(): Observable<{ success: boolean; summary: AlertSummary }> {
        return this._httpClient.get<{ success: boolean; summary: AlertSummary }>(
            `${this._baseUrl}/my-alerts/summary`
        ).pipe(
            tap((response) => {
                this._alertSummary.next(response.summary);
            })
        );
    }

    /**
     * Get alert summary for a specific project
     * @param projectId - Project ID
     * @returns Observable of alert summary statistics
     */
    getProjectAlertSummary(projectId: number): Observable<{ success: boolean; summary: AlertSummary }> {
        return this._httpClient.get<{ success: boolean; summary: AlertSummary }>(
            `${this._baseUrl}/projects/${projectId}/alerts/summary`
        ).pipe(
            tap((response) => {
                this._alertSummary.next(response.summary);
            })
        );
    }

    /**
     * Get alerts for a specific project
     * @param projectId - Project ID
     * @param filters - Optional filters
     * @returns Observable of paginated alerts
     */
    getProjectAlerts(projectId: number, filters?: AlertFilters): Observable<PaginatedAlertsResponse> {
        let httpParams = new HttpParams();
        
        if (filters) {
            Object.entries(filters).forEach(([key, value]) => {
                if (value !== undefined && value !== null && value !== '') {
                    // Handle severity as array (multiple values)
                    if (key === 'severity' && typeof value === 'string' && value.includes(',')) {
                        // Split comma-separated values and add multiple parameters
                        value.split(',').forEach(sev => {
                            httpParams = httpParams.append('severity', sev.trim());
                        });
                    } else {
                        httpParams = httpParams.set(key, value.toString());
                    }
                }
            });
        }
        
        // Debug: Log the constructed URL and params
        const url = `${this._baseUrl}/projects/${projectId}/alerts`;
        console.log('🌐 API Request URL:', url);
        console.log('🌐 API Request Params:', httpParams.toString());
        console.log('🌐 Full URL:', url + '?' + httpParams.toString());
        
        return this._httpClient.get<PaginatedAlertsResponse>(
            url,
            { params: httpParams }
        ).pipe(
            tap((response) => {
                this._alerts.next(response.data);
                console.log('✅ API Response received:', response.data.length, 'alerts');
                console.log('✅ API says filters_applied:', response.filters_applied || 'Not provided by API');
            })
        );
    }

    /**
     * Mark alert as read
     * @param source - Alert source ('geographic' or 'security_event')
     * @param id - Alert ID
     * @returns Observable of updated alert
     */
    markAsRead(source: string, id: number): Observable<{ success: boolean; data: UnifiedAlert }> {
        return this._httpClient.patch<{ success: boolean; data: UnifiedAlert }>(
            `${this._baseUrl}/alerts/${source}/${id}/read`,
            {}
        );
    }

    /**
     * Update alert status
     * @param source - Alert source
     * @param id - Alert ID
     * @param status - New status
     * @param notes - Optional resolution notes
     * @returns Observable of updated alert
     */
    updateAlertStatus(
        source: string, 
        id: number, 
        status: string, 
        notes?: string
    ): Observable<{ success: boolean; data: UnifiedAlert }> {
        // Security event alerts use 'state' field, geographic alerts use 'status' field
        const body: any = source === 'security_event' 
            ? { state: status }
            : { status };
        
        if (notes) {
            body.notes = notes;
        }
        
        return this._httpClient.patch<{ success: boolean; data: UnifiedAlert }>(
            `${this._baseUrl}/alerts/${source}/${id}/status`,
            body
        );
    }

    /**
     * Delete an alert
     * @param source - Alert source
     * @param id - Alert ID
     * @returns Observable of success response
     */
    deleteAlert(source: string, id: number): Observable<{ success: boolean; message: string }> {
        return this._httpClient.delete<{ success: boolean; message: string }>(
            `${this._baseUrl}/alerts/${source}/${id}`
        );
    }

    /**
     * Bulk update alerts (unified endpoint)
     * @param action - Bulk action with alert IDs and action type
     * @returns Observable of bulk operation result
     */
    bulkUpdate(action: BulkAlertAction): Observable<{ success: boolean; results: any }> {
        return this._httpClient.post<{ success: boolean; results: any }>(
            `${this._baseUrl}/alerts/bulk-update`,
            {
                alerts: action.alert_ids,
                action: action.action,
                notes: action.notes
            }
        );
    }

    /**
     * Bulk mark alerts as read
     * @param alertIds - Array of alert IDs with source
     * @returns Observable of bulk operation result
     */
    bulkMarkAsRead(alertIds: Array<{ source: string; id: number }>): Observable<{ success: boolean; results: any }> {
        return this.bulkUpdate({
            alert_ids: alertIds,
            action: 'mark_read'
        });
    }

    /**
     * Bulk acknowledge alerts
     * @param alertIds - Array of alert IDs with source
     * @param notes - Optional notes
     * @returns Observable of bulk operation result
     */
    bulkAcknowledge(alertIds: Array<{ source: string; id: number }>, notes?: string): Observable<{ success: boolean; results: any }> {
        return this.bulkUpdate({
            alert_ids: alertIds,
            action: 'acknowledge',
            notes: notes
        });
    }

    /**
     * Bulk resolve alerts
     * @param alertIds - Array of alert IDs with source
     * @param notes - Optional notes
     * @returns Observable of bulk operation result
     */
    bulkResolve(alertIds: Array<{ source: string; id: number }>, notes?: string): Observable<{ success: boolean; results: any }> {
        return this.bulkUpdate({
            alert_ids: alertIds,
            action: 'resolve',
            notes: notes
        });
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Enhanced Dashboard Widgets Methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Get alert trend data for the last N days
     * @param days - Number of days to retrieve (7, 30, 90, etc.)
     * @returns Observable of alert trend data
     */
    getAlertTrend(days: number = 30): Observable<any> {
        const httpParams = new HttpParams().set('days', days.toString());
        return this._httpClient.get<any>(
            `${this._baseUrl}/my-alerts/trend`,
            { params: httpParams }
        );
    }

    /**
     * Get top alert types
     * @param limit - Number of top types to retrieve
     * @returns Observable of top alert types
     */
    getTopAlertTypes(limit: number = 10): Observable<any> {
        const httpParams = new HttpParams().set('limit', limit.toString());
        return this._httpClient.get<any>(
            `${this._baseUrl}/my-alerts/by-type`,
            { params: httpParams }
        );
    }

    /**
     * Get alerts distribution by country
     * @param limit - Number of top countries to retrieve (default: 15)
     * @returns Observable of alerts by country
     */
    getAlertsByCountry(limit: number = 15): Observable<any> {
        const httpParams = new HttpParams().set('limit', limit.toString());
        return this._httpClient.get<any>(
            `${this._baseUrl}/my-alerts/by-country`,
            { params: httpParams }
        );
    }

    /**
     * Get recent critical alerts (CRITICAL + HIGH)
     * @param limit - Number of alerts to retrieve
     * @returns Observable of recent critical and high alerts
     */
    getRecentCriticalAlerts(limit: number = 5): Observable<PaginatedAlertsResponse> {
        const filters: AlertFilters = {
            severity: 'critical,high' as any,  // Will be split into multiple params by getMyAlerts
            limit: limit,
            page: 1,
            sort: 'created_at',
            order: 'desc'
        };
        return this.getMyAlerts(filters);
    }

    /**
     * Get alerts (CRITICAL + HIGH severity only) - Default view
     * @param filters - Optional additional filters
     * @returns Observable of paginated CRITICAL and HIGH alerts
     */
    getAlerts(filters?: Partial<AlertFilters>): Observable<PaginatedAlertsResponse> {
        const defaultFilters: AlertFilters = {
            severity: 'critical,high' as any,  // Will be split into multiple params
            limit: filters?.limit || 25,
            page: filters?.page || 1,
            sort: filters?.sort || 'created_at',
            order: filters?.order || 'desc',
            ...filters
        };
        return this.getMyAlerts(defaultFilters);
    }

    /**
     * Get alerts for a specific honeytoken
     * @param honeytokenId - Honeytoken ID
     * @param filters - Optional filters (severity, status, limit, page, etc.)
     * @returns Observable of paginated alerts for the honeytoken
     */
    getHoneytokenAlerts(
        honeytokenId: number,
        filters?: AlertFilters
    ): Observable<PaginatedAlertsResponse> {
        let httpParams = new HttpParams();

        if (filters) {
            Object.entries(filters).forEach(([key, value]) => {
                if (value !== undefined && value !== null && value !== '') {
                    // Handle severity as array (multiple values)
                    if (key === 'severity' && typeof value === 'string' && value.includes(',')) {
                        // Split comma-separated values and add multiple parameters
                        value.split(',').forEach(sev => {
                            httpParams = httpParams.append('severity', sev.trim());
                        });
                    } else {
                        httpParams = httpParams.set(key, value.toString());
                    }
                }
            });
        }

        return this._httpClient.get<PaginatedAlertsResponse>(
            `${this._baseUrl}/honeytokens/${honeytokenId}/alerts`,
            { params: httpParams }
        );
    }

    /**
     * Get alerts for a specific security event
     * @param securityEventId - Security Event ID
     * @param filters - Optional filters (severity, status, limit, page, etc.)
     * @returns Observable of alerts array for the security event
     */
    getSecurityEventAlerts(
        securityEventId: number,
        filters?: AlertFilters
    ): Observable<UnifiedAlert[]> {
        let httpParams = new HttpParams();

        if (filters) {
            Object.entries(filters).forEach(([key, value]) => {
                if (value !== undefined && value !== null && value !== '') {
                    httpParams = httpParams.set(key, value.toString());
                }
            });
        }

        return this._httpClient.get<any>(
            `${this._baseUrl}/alerts/by-security-event-id/${securityEventId}`,
            { params: httpParams }
        ).pipe(
            tap((rawResponse) => {
                console.log('🔍 Raw API response from getSecurityEventAlerts:', rawResponse);
                if (Array.isArray(rawResponse) && rawResponse.length > 0) {
                    console.log('🔍 First alert structure:', rawResponse[0]);
                } else if (rawResponse?.data && rawResponse.data.length > 0) {
                    console.log('🔍 First alert structure:', rawResponse.data[0]);
                }
            }),
            map((response: any) => {
                // Handle both array response and {success, data} response
                const alertsArray = Array.isArray(response) ? response : (response.data || []);
                
                // Normalize each alert to ensure consistent field names
                return alertsArray.map((alert: any) => ({
                    ...alert,
                    // Ensure alert_type is populated (may come as 'type' from API)
                    alert_type: alert.alert_type || alert.type || 'unknown',
                    // Ensure confidence_score exists and convert to percentage if needed
                    // API may return: probability (0-1), confidence (0-1), or confidence_score (0-100)
                    confidence_score: (() => {
                        if (alert.confidence_score !== undefined && alert.confidence_score !== null) {
                            return alert.confidence_score;
                        }
                        if (alert.probability !== undefined && alert.probability !== null) {
                            // Convert probability (0-1) to percentage (0-100)
                            return alert.probability * 100;
                        }
                        if (alert.confidence !== undefined && alert.confidence !== null) {
                            // Convert confidence (0-1) to percentage (0-100)
                            return alert.confidence * 100;
                        }
                        return null;
                    })(),
                    // Ensure created_at is populated (may come as 'datetime' from API)
                    created_at: alert.created_at || alert.datetime
                }));
            }),
            tap((normalizedAlerts) => {
                console.log('✅ Normalized alerts:', normalizedAlerts);
                if (normalizedAlerts.length > 0) {
                    console.log('✅ First normalized alert:', normalizedAlerts[0]);
                    console.log('✅ Confidence score (should be 0-100):', normalizedAlerts[0].confidence_score);
                }
            })
        );
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Geographic Alerts Methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Get geographic alerts dashboard with aggregated statistics
     * @returns Observable of geographic dashboard data
     */
    getGeographicAlertsDashboard(): Observable<GeographicAlertsDashboard> {
        return this._httpClient.get<GeographicAlertsDashboard>(
            `${this._baseUrl}/geographic-alerts/dashboard`
        ).pipe(
            tap((response) => {
                console.log('🌍 Geographic Dashboard:', response.data);
            })
        );
    }

    /**
     * Get geographic alerts with filters
     * @param filters - Optional filters for geographic alerts
     * @returns Observable of paginated geographic alerts
     */
    getGeographicAlerts(filters?: GeographicAlertFilters): Observable<PaginatedAlertsResponse> {
        let httpParams = new HttpParams();
        
        if (filters) {
            Object.entries(filters).forEach(([key, value]) => {
                if (value !== undefined && value !== null && value !== '') {
                    // Handle severity as array (multiple values)
                    if (key === 'severity' && typeof value === 'string' && value.includes(',')) {
                        value.split(',').forEach(sev => {
                            httpParams = httpParams.append('severity', sev.trim());
                        });
                    } else {
                        httpParams = httpParams.set(key, value.toString());
                    }
                }
            });
        }

        const url = `${this._baseUrl}/geographic-alerts`;
        console.log('🌍 Geographic Alerts URL:', url + '?' + httpParams.toString());
        
        return this._httpClient.get<PaginatedAlertsResponse>(
            url,
            { params: httpParams }
        ).pipe(
            tap((response) => {
                console.log('✅ Geographic Alerts received:', response.data.length);
            })
        );
    }

    /**
     * Get geographic alert details by ID
     * @param id - Geographic alert ID
     * @returns Observable of detailed geographic alert
     */
    getGeographicAlertDetails(id: number): Observable<{ success: boolean; data: GeographicAlert }> {
        return this._httpClient.get<{ success: boolean; data: GeographicAlert }>(
            `${this._baseUrl}/geographic-alerts/${id}`
        ).pipe(
            tap((response) => {
                console.log('🌍 Geographic Alert Details:', response.data);
            })
        );
    }

    /**
     * Update geographic alert status with proper status values
     * @param id - Geographic alert ID
     * @param status - New status (new, acknowledged, investigating, resolved, false_positive)
     * @param notes - Optional resolution notes
     * @returns Observable of updated alert
     */
    updateGeographicAlertStatus(
        id: number,
        status: GeographicAlertStatus,
        notes?: string
    ): Observable<{ success: boolean; data: GeographicAlert }> {
        const body: any = { status };
        
        if (notes) {
            body.notes = notes;
        }
        
        return this._httpClient.patch<{ success: boolean; data: GeographicAlert }>(
            `${this._baseUrl}/alerts/geographic/${id}/status`,
            body
        );
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Single Alert Retrieval
    // -----------------------------------------------------------------------------------------------------

    /**
     * Get single alert by ID (legacy endpoint)
     * @param id - Alert ID
     * @returns Observable of alert
     */
    getAlertById(id: number): Observable<UnifiedAlert> {
        return this._httpClient.get<any>(
            `${this._baseUrl}/alerts/${id}`
        ).pipe(
            map((response) => response.data || response),
            tap((alert) => {
                console.log('📋 Single Alert retrieved:', alert);
            })
        );
    }
}
